import type { Channel } from 'phoenix'

/**
 * Core implementation for channel joining with timeout.
 * Uses function overloads to provide type-safe response handling.
 */
function joinChannelCore<TResponse>(
  channel: Channel,
  timeout: number,
  captureResponse: true,
  onRejoin?: () => void
): Promise<TResponse>

function joinChannelCore(
  channel: Channel,
  timeout: number,
  captureResponse: false,
  onRejoin?: () => void
): Promise<void>

function joinChannelCore<TResponse = unknown>(
  channel: Channel,
  timeout: number,
  captureResponse: boolean,
  onRejoin?: () => void
): Promise<TResponse | void> {
  return Promise.race([
    new Promise<TResponse | void>((resolve, reject) => {
      const push = channel.join()
      push.receive('ok', (response) => {
        resolve(captureResponse ? response : undefined)
      })
      push.receive('error', (error) => { reject(error) })
      push.receive('timeout', () => { reject(new Error('Channel join timeout from Phoenix')) })

      // Silent rejoin signal. phoenix-js recycles the SAME join push on
      // reconnect (`Channel.rejoin` -> `joinPush.resend`, and `Push.reset`
      // deliberately keeps `recHooks`), so a hook registered here fires again
      // on every later successful join — including the ones the channel does
      // on its own timer, which no store action ever observes. The first 'ok'
      // is the original join and is skipped; only true REjoins get through.
      if (onRejoin) {
        let hasJoinedOnce = false
        push.receive('ok', () => {
          if (!hasJoinedOnce) {
            hasJoinedOnce = true
            return
          }
          onRejoin()
        })
      }
    }),
    new Promise<TResponse | void>((_, reject) =>
      setTimeout(() => { reject(new Error('Channel join timeout')) }, timeout)
    )
  ])
}

/**
 * Helper to handle Phoenix channel join with timeout.
 * Does not retry - retries should be handled at a higher level (rejoinAllChannels).
 * This prevents the join/leave/join patterns caused by creating new channels on each retry.
 */
export const joinChannelWithTimeout = async (
  channel: Channel,
  timeout = 7_000
): Promise<void> => {
  return joinChannelCore(channel, timeout, false)
}

/**
 * Helper to handle Phoenix channel join with timeout and response capture.
 * Returns the response data from successful joins.
 *
 * `onRejoin` fires on every SUBSEQUENT successful join of this same channel
 * (phoenix-js's own reconnect rejoins included) — never for the initial one,
 * which the returned promise already reports.
 */
export const joinChannelWithResponse = async <TResponse = unknown>(
  channel: Channel,
  timeout = 7_000,
  onRejoin?: () => void
): Promise<TResponse> => {
  return joinChannelCore<TResponse>(channel, timeout, true, onRejoin)
}